import { BitkitSwitch, BitkitTooltip, IconEye } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { useSlotRecipe } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';

import useWatchApp from './hooks/useWatchApp';

// Bitkit icons ship a different SVG per size, so the sidebar item renders both and hides one.
const showAtDesktop = { display: { base: 'none', tablet: 'block' } };
const showAtMobile = { display: { base: 'block', tablet: 'none' } };

/**
 * The watch switch, as a row of the project sidebar.
 *
 * The design draws this as a sidebar item with the switch where the link icon goes, and the
 * measurements agree with the `pageSidebar` recipe to the pixel: a 40px row, 8 block and 24 inline
 * padding, 12 between icon and label, a 32x16 control against the trailing edge. It still cannot be
 * a `BitkitPageSidebar.Item`, because that item's `suffixIcon` is a `BitkitIconComponent` it renders
 * itself with a `size` -- there is no slot a control can go in.
 *
 * The row is ours and the switch is untouched: a plain `Box` carries the sidebar item's styling
 * from the `pageSidebar` recipe -- height, padding, hover, icon and label -- and `BitkitSwitch`
 * sits inside it with behaviour props only, no styling of its own. Before this the switch itself
 * was dressed up as the row with nine layout props copied off the item, and had drifted: 12 block
 * padding against the item's 8 at desktop, 32 inline against 24, a 16 icon gap against 12, and no
 * hover state at all.
 *
 * `Switch.Root` renders a `<label>` whose hidden input is named by a label element the switch owns,
 * and neither `aria-label` nor Zag's `ids` reaches that input from outside -- both land on the root,
 * which is the `<label>` itself. So the switch keeps its own label and hides it with `hideLabel`,
 * while the row shows the visible text with `aria-hidden` on it. Announced once, not twice.
 *
 * Clicking anywhere on the row that is not the switch forwards to that input, which is what the
 * padding on the switch used to buy. `group` is what the item icon's `_groupHover` hangs off, so the
 * eye brightens with the row like every neighbouring icon.
 *
 * The recipe key is a string, so a rename in bitkit-v2 would break this silently. This is the only
 * place that borrows it; the row belongs upstream once a switch has somewhere to go in `.Item`.
 */
type WatchAppToggleProps = BoxProps & { appSlug: string; isInitialWatching: boolean };

const WatchAppToggle = ({ appSlug, isInitialWatching, ...rest }: WatchAppToggleProps) => {
  const { isLoading, isWatching, toggleWatching } = useWatchApp(appSlug, isInitialWatching);
  const styles = useSlotRecipe({ key: 'pageSidebar' })({});

  return (
    <BitkitTooltip text="Receive email notifications related to this project.">
      <Box
        className="group"
        css={styles.item}
        onClick={(event) => {
          // The switch's own label handles clicks that land on it; this is the rest of the row.
          if (!(event.target as HTMLElement).closest('label')) {
            event.currentTarget.querySelector('input')?.click();
          }
        }}
        {...rest}
      >
        {/* The switch below carries the accessible name, so this copy is decorative. */}
        <Text aria-hidden as="span" css={styles.contentBlock}>
          <IconEye css={[styles.itemIcon, showAtDesktop]} size="16" />
          <IconEye css={[styles.itemIcon, showAtMobile]} size="24" />
          <Text as="span" css={styles.itemLabel}>
            Watching
          </Text>
        </Text>
        <BitkitSwitch
          checked={isWatching}
          // `hideLabel` rather than a screen-reader-only node of our own: an off-screen child still
          // sits in the root's flex flow, so its `gap` left the control 8 short of the trailing edge
          // that every neighbouring row's suffix icon lines up on.
          hideLabel
          label="Watching"
          onCheckedChange={toggleWatching}
          size="md"
          state={isLoading ? 'disabled' : undefined}
        />
      </Box>
    </BitkitTooltip>
  );
};

export default WatchAppToggle;
